import { applyTransaction, CommandError, type Transaction } from './commands.ts';
import type { Project } from './types.ts';

/** Undo/redo stack around a project. Immutable snapshots, so cheap to keep. */
export class History {
  private undoStack: Transaction[] = [];
  private redoStack: Transaction[] = [];
  private listeners = new Set<(p: Project) => void>();
  private txListeners = new Set<(tx: Transaction) => void>();
  private _project: Project;
  private readonly limit: number;
  /** Viewers of a shared or read-only cloud project: every change is refused. */
  readOnly = false;

  constructor(project: Project, limit = 500) {
    this._project = project;
    this.limit = limit;
  }

  get project(): Project {
    return this._project;
  }
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
  get undoLabel(): string | undefined {
    return this.undoStack.at(-1)?.label;
  }

  subscribe(fn: (p: Project) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Called with every transaction applied locally (do, undo, redo), in order: what a sync
   * session sends to the server.
   */
  subscribeTransactions(fn: (tx: Transaction) => void): () => void {
    this.txListeners.add(fn);
    return () => this.txListeners.delete(fn);
  }

  /**
   * Replace the project with a version that includes other people's changes (live sync).
   * The undo stack is kept: an entry that no longer applies is dropped when reached.
   */
  replace(project: Project): void {
    this.set(project);
  }

  /** Apply a transaction and record it. */
  dispatch(tx: Transaction): void {
    if (tx.ops.length === 0 || this.readOnly) return;
    const { project, inverse } = applyTransaction(this._project, tx);
    this.undoStack.push(inverse);
    if (this.undoStack.length > this.limit) this.undoStack.shift();
    this.redoStack = [];
    this.set(project);
    this.emitTx(tx);
  }

  /** Replace the whole project (open file, new project). Clears history. */
  reset(project: Project): void {
    this.undoStack = [];
    this.redoStack = [];
    this.set(project);
  }

  /** Undo the last step. Returns false when it no longer applies (changed by someone else). */
  undo(): boolean {
    return this.step(this.undoStack, this.redoStack);
  }

  redo(): boolean {
    return this.step(this.redoStack, this.undoStack);
  }

  private step(from: Transaction[], to: Transaction[]): boolean {
    if (this.readOnly) return true;
    const tx = from.pop();
    if (!tx) return true;
    try {
      const { project, inverse } = applyTransaction(this._project, tx);
      to.push(inverse);
      this.set(project);
      this.emitTx(tx);
      return true;
    } catch (e) {
      if (e instanceof CommandError) return false; // dropped: the items it touches were changed remotely
      throw e;
    }
  }

  private emitTx(tx: Transaction): void {
    for (const fn of this.txListeners) fn(tx);
  }

  private set(p: Project): void {
    this._project = p;
    for (const fn of this.listeners) fn(p);
  }
}
